import { AsyncValidatorFn, AbstractControl, ValidationErrors, Validators } from '@angular/forms';
import { of, map, Observable, switchMap, take, tap, catchError, first, timer } from 'rxjs';
import { debounceTime, distinctUntilChanged, startWith } from 'rxjs/operators';
import { ThemingService } from '../services/theming.service';
import { LocationService } from '../services/location.service';
import { UserService } from '../services/user.service';
import { Router } from '@angular/router';
import { IThemeDetails } from '../interfaces/themeDetail';
import { GroupService } from '../services/group.service';

export interface ValidationState {
  [key: string]: {
    isValid: boolean;
    errorMessage?: string;
    isChecking: boolean;
  };
}

export class AppFieldValidators extends Validators {
  /// <summary>
  /// Validates that the auto complete input value is selected from the list.
  /// </summary>
  static isAutoCompleteSelected(control: AbstractControl): ValidationErrors | null {
    if (control.value === null || control.value === undefined) return null;

    const value = control['isValueFromAutoComplete'];
    const valid = value === true;
    const rtn = valid ? null : { isAutoCompleteSelected: true };
    return rtn;
  }
}

export class AppAsyncFieldValidators {
  static orgId: number = 0;
  static userNameCache: Map<string, ValidationErrors | null> = new Map();
  static userNameCacheTime: Map<string, number> = new Map();

  static groupNameValidator(_gs: GroupService, orgId: number): AsyncValidatorFn {
    AppAsyncFieldValidators.orgId = orgId;
    return (control: AbstractControl) => {
      const orgId = AppAsyncFieldValidators.orgId;

      if (!control.value || orgId === 0 || control.value === control['originalValue'])
        return of(null);

      return _gs.groupNameAlreadyExists(orgId, control.value).pipe(
        map(result => {
          if (result === true) {
            return { groupNameExists: true };
          } else {
            return null;
          }
        })
      );
    };
  }

  static websiteUrlFormatter(): AsyncValidatorFn {
    return (control: AbstractControl): Observable<ValidationErrors | null> => {
      if (!control.value) {
        return of(null);
      }

      let value = control.value.trim().toLowerCase();

      try {
        // Try to parse as URL to validate format
        let url: URL;

        if (!value.startsWith('http://') && !value.startsWith('https://')) {
          url = new URL('https://' + value);
        } else {
          url = new URL(value);
        }

        // Extract hostname and port
        let formattedValue = url.hostname;
        const port = url.port;

        // Remove 'www.' if present
        if (formattedValue.startsWith('www.')) {
          formattedValue = formattedValue.substring(4);
        }

        // Validate hostname format (alphanumeric, hyphens, dots)
        // Modified regex to make TLD optional and support local domains
        const hostnameRegex = /^[a-z0-9]+([\-\.]{1}[a-z0-9]+)*(\.[a-z]{2,})?$/;

        if (!hostnameRegex.test(formattedValue)) {
          return of({ invalidDomain: true });
        }

        // Add port back to formatted value if it exists
        if (port) {
          formattedValue = `${formattedValue}:${port}`;
        }

        // If the value is different from original, update the control
        if (formattedValue !== value) {
          setTimeout(() => {
            control.setValue(formattedValue, {
              emitEvent: false,
              onlySelf: true,
            });
          });
        }

        return of(null);
      } catch (e) {
        // If URL parsing fails, return validation error
        return of({ invalidUrl: true });
      }
    };
  }

  static websiteNameValidator(
    themingService: ThemingService,
    themeModel: IThemeDetails,
    router: Router
  ): AsyncValidatorFn {
    //custom validation for the website name
    return (control: AbstractControl) => {
      //is optional, if not set, then return null
      if (!control.value || control.value === themeModel.companyUrl) {
        return of(null);
      }

      let value = control.value;

      //validate is alphanumeric
      const regx = new RegExp('^[a-zA-Z0-9_]*$');
      if (!regx.test(value)) {
        return of({ invalidCharacterValidation: true });
      }

      //ensure the value entered is not a reserved word for routes in the app
      const routes = router.config.map(r => r.path);
      if (routes.includes(value)) {
        return of({ websiteNameExists: true });
      }

      //validate theme does not exists with the same name
      return themingService.customerUrlExists(themeModel.billableOrganizationId, value).pipe(
        map(result => {
          if (result === true) {
            return { websiteNameExists: true };
          } else {
            return null;
          }
        })
      );
    };
  }

  static userNameValidator(_us: UserService): AsyncValidatorFn {
    //custom validation for the username
    return (control: AbstractControl) => {
      //is optional, if not set, then return null
      if (!control.value || control.value === control['originalValue']) {
        return of(null).pipe(take(1));
      }

      const userName = control.value.trim().toLowerCase();
      const cacheTime = AppAsyncFieldValidators.userNameCacheTime.get(userName);
      //check if the username is already in the cache
      if (
        AppAsyncFieldValidators.userNameCache.has(userName) &&
        cacheTime &&
        Date.now() - cacheTime < 100000
      ) {
        return of(AppAsyncFieldValidators.userNameCache.get(userName)).pipe(take(1));
      }

      //validate username does not exists with the same name
      return _us.selectedUser$.pipe(
        first(),
        switchMap(user => {
          //if the username is the same as the original value, then return null
          if (user && user.userDetails.userName === control.value) {
            return of(null).pipe(take(1));
          }
          //validate username does not exists with the same name
          return _us.userNameAlreadyExists(control.value).pipe(
            map(result => {
              const validationResult = result === true ? { userNameAlreadyExists: true } : null;
              AppAsyncFieldValidators.userNameCache.set(userName, validationResult);
              AppAsyncFieldValidators.userNameCacheTime.set(userName, Date.now());
              return validationResult;
            }),
            catchError(error => {
              console.error('Error validating username', error);
              return of(null);
            })
          );
        })
      );
    };
  }

  static previousLocationName = '';
  static locationNameDoesNotAlreadyExist(
    locationService: LocationService,
    data: { organizationId: number | null; excludeLocationId?: number },
    callbackFn: () => void
  ): AsyncValidatorFn {
    return (control: AbstractControl): Observable<ValidationErrors | null> => {
      const currentValue = control.value?.toString?.().trim?.().toLowerCase?.();
      const originalValue = control['originalValue']?.toString?.().trim?.().toLowerCase?.();

      if (currentValue && originalValue && currentValue === originalValue) {
        return of(null);
      }

      if (!data?.organizationId || !control.value) return of(null);

      const locationName = control.value?.trim();
      if (!locationName) return of(null);

      return timer(300).pipe(
        switchMap(() => locationService
            .locationNameAlreadyExistsForOrg(
              data.organizationId,
              locationName,
              data.excludeLocationId
            )),
              tap(exists => { if(exists && AppAsyncFieldValidators.previousLocationName !== locationName && callbackFn) callbackFn(); }),
              tap(() => AppAsyncFieldValidators.previousLocationName = locationName),
              map(exists => (exists ? { locationNameAlreadyExists: true } : null)),
              take(1));
    };
  }
}
